<template>
  <div :class="['post-page-wrapper', postType]">
    <div v-if="post" class="post-content desk-hor-mob-col">
      <div class="post-dynamic-content flex-col">
        <BreadCrumbs
          :crumbs="[
            { title: 'Community', url: '/community' },
            { title: displayPostType, url: `/community/${postType}` },
          ]"
        />
        <h2 class="main-category">
          <NuxtLink
            v-if="mainCategory"
            :to="localePath(`/community/${postType}${mainCategory.url}`)"
            class="community-category"
          >
            {{ mainCategory.display_title }}
          </NuxtLink>
        </h2>

        <h1 class="post-title h3">
          {{ post.display_title }}
        </h1>
        <span v-if="!isEvent && publishedDate" class="event-date">{{ publishedDate }}</span>
        <SocialShareIcons v-if="post.seo" :share-text="post.seo.open_graph_title || post.seo.page_title || ''" />
        <em v-if="post && post.author" class="author">By {{ post.author }}</em>
        <Video
          v-if="isVideo"
          :preload="true"
          :asset="props.post?.featured_image?.[0]"
          :options="imageOptions"
          :video-classes-override="['comm-video']"
        />
        <CloudinaryImage
          v-else
          :public-id="props.post?.featured_image?.[0]?.public_id"
          :options="imageOptions"
          :alt="post.display_title"
          :image-classes-override="['header-image', 'mob-full-content']"
          :aspect-ratio-override="'auto'"
        />
        <PageSection v-if="sections" :section="sections" render-mode="blog" />
      </div>
      <div class="right-rail-content" :class="postType">
        <template v-if="isEvent">
          <div class="flex-col event-data">
            <h5 class="flex-inline date-header"><CalendarEventLine /> Date & Time</h5>
            <div class="event-date rr">
              {{ eventDateDisplay }}
            </div>
          </div>
          <CustomButton v-if="post.event_link" :is-small="true" style-color="black" @click-button="buttonClick">
            {{ post.event_link.title || 'Watch on Facebook' }}
          </CustomButton>
        </template>
        <template v-else>
          <template v-if="filteredRelatedPosts && filteredRelatedPosts.length">
            <h4 class="related-section-title">{{ isVideo ? 'Related' : 'Featured' }} {{ postType }}s</h4>
            <template v-for="relPost in filteredRelatedPosts" :key="relPost.url">
              <PostItem :post="relPost" :is-secondary-featured="true" />
            </template>
          </template>
          <EmailSignUp v-if="signUpFooter && isBlog" v-bind="signUpFooter" class="desk-only comm-email" />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useNavStore } from '~/store/nav';
import { getFirstIndex } from '~/util/arrays';
import { extractComponent } from '~/util/contentstack/csHelpers';
import { CTSPost, CommunityCategory } from '~/types/contentstack';
import { formatStartDate, startAndEndDate } from '~/util/date'; // TODO - move to comp-lib
import { useDeviceStore } from '~/store/device';

const deviceStore = useDeviceStore();
const localePath = useLocalePath();
const navStore = useNavStore();

const props = defineProps({
  post: {
    type: Object as () => CTSPost,
    required: true,
  },
  postType: {
    type: String as () => 'event' | 'video' | 'blog',
    required: true,
  },
  displayPostType: {
    type: String as () => 'Events' | 'Videos' | 'Blog',
    required: true,
  },
  relatedPosts: {
    type: Array as () => CTSPost[],
    required: false,
    default: () => [],
  },
  mainCategory: {
    type: Object as () => CommunityCategory,
    required: true,
  },
});

const isVideo = computed<boolean>(() => props.postType === 'video');
const isBlog = computed<boolean>(() => props.postType === 'blog');
const isEvent = computed<boolean>(() => props.postType === 'event');
const footerComputed = computed<any>(() => getFirstIndex(navStore.footer?.items));
const signUpFooter = computed<any>(() => extractComponent((footerComputed.value as any)?.email_sign_upConnection));
const sections = computed<{ [index: string]: any }>(() => (props.post as any)?.sections || []);
// const categories = computed<any[]>(() => extractItems(props.post?.categoriesConnection));
const filteredRelatedPosts = computed(() => {
  const filtered = props.relatedPosts?.filter((item: CTSPost) => item?.url !== props.post?.url);
  const sliced = filtered.slice(0, 5);
  return sliced;
});
const publishedDate = computed<string | undefined>(() =>
  props.post?.publish_date ? formatStartDate(props.post.publish_date as string) : undefined
);
const imageOptions = computed(() => {
  return {
    width: deviceStore.isMobile ? 480 : deviceStore.isTablet ? 1024 : 800,
    height: deviceStore.isMobile ? 485 : deviceStore.isTablet ? 576 : 450,
    quality: 'auto',
  };
});
const eventDateDisplay = computed<string | undefined>(() =>
  props.post?.event_date
    ? props.post?.event_end_date
      ? startAndEndDate(props.post.event_date || '', props.post.event_end_date)
      : formatStartDate(props.post.event_date || '', true)
    : undefined
);

function buttonClick() {
  window.open(props.post?.event_link?.href);
}
</script>

<style lang="scss" scoped>
// START VIDEO SPECIFIC
:deep(.video-wrapper) {
  .video-container {
    padding: 0;
  }
}
// END VIDEO SPECIFIC

// START EVENT SPECIFIC
.date-header {
  :deep(svg) {
    margin-right: 8px;
  }
}
.post-page-wrapper.event {
  .event-date.rr {
    margin-bottom: 1.5rem;
    margin-left: 2rem;
  }
}
// END EVENT SPECIFIC
.post-page-wrapper {
  width: 100%;
  max-width: calc(local-vars.$xxl-content-max-width - 4rem);
  margin: #{local-functions.rem-calc(15)} auto;
  @media (min-width: local-vars.$xxl-content-max-width) and (max-width: 1655px) {
    margin: 0 1rem;
  }
}
.post-dynamic-content {
  width: 100%;
  .breadcrumbs,
  .main-category,
  .post-title,
  .event-date,
  .icon-wrapper {
    padding: 0 0.75rem;
  }
}
:deep(.header-image) {
  height: auto;
  margin-bottom: 1.5rem;
  @include local-mixins.desktop {
    width: 100%;
    max-width: #{local-functions.rem-calc(800)};
    border-radius: 8px;
  }
}
.post-title {
  margin-top: 0.25rem;
  @include local-mixins.desktop {
    @include local-mixins.h2;
  }
}
.main-category {
  font-size: unset;
}
.related-section-title {
  margin-bottom: 1rem;
}
.author {
  margin-bottom: 1rem;
}
.event-date {
  font-size: 0.875rem;
  color: $color-neutral-cool-600;
  margin-bottom: 1rem;
}
:deep(.section-wrapper) .content-block {
  img {
    width: 100%;
    height: auto;
  }
}
@include local-mixins.tablet_and_mobile {
  .post-content {
    margin-top: 1rem;
  }
  .right-rail-content {
    margin-top: 2rem;
    padding: 0 0.75rem;
  }
}
@include local-mixins.desktop {
  .post-content {
    justify-content: center;
  }
  .post-dynamic-content {
    max-width: #{local-functions.rem-calc(800)};
    padding: 0;
  }
  :deep(.section-wrapper) {
    &.xxl-full-content {
      width: 100%;
    }
  }
  .right-rail-content {
    margin-left: 6rem;
    width: 100%;
    max-width: #{local-functions.rem-calc(350)};
    margin-top: 176px;
    &.event {
      margin-top: 154px;
    }
  }
  :deep(.email-sign-up-wrapper.comm-email) {
    padding: 2rem 0;
    border-radius: 8px;
    background-color: $color-neutral-cool-900 !important;
    color: $color-neutral-white;
    margin-top: 40px;
    .email-sign-up-content,
    .email-input {
      flex-direction: column;
    }
    .email-input,
    .email-offer-header,
    .btn {
      width: 100%;
      max-width: 100%;
      margin: 0;
    }
    .btn {
      margin-top: 1rem;
    }
  }
}
</style>
